// @ts-nocheck
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages Gift Finder header filter dropdowns.
 *
 * On each select change this component:
 *  1. Starts from the *current* browser URL (preserving sort, price, and
 *     every other filter already active via the normal filters form).
 *  2. Merges only the GIFTFINDER select values into that URL —
 *     deleting the param when the "all" option is chosen.
 *  3. Hands the merged URL to FacetsFormComponent.updateFiltersByURL(),
 *     which pushes it to history and re-renders only the target section.
 *
 * This avoids the `updateFilters()` path, which wipes url.search and
 * rebuilds it from our partial form alone — removing every non-GIFTFINDER
 * param and fighting native facets in the collection section.
 *
 * The normal filters block (amb-filter-normal) remains responsible for all
 * non-GIFTFINDER filters; this script never touches those params.
 *
 * @extends {Component}
 */
class GiftcardFilterHeaderComponent extends Component {
  connectedCallback() {
    super.connectedCallback?.();

    this.#wireSectionId();

    this.querySelectorAll('[data-gift-filter-select]').forEach((select) => {
      select.addEventListener('change', this.#handleSelectChange);
    });

    // Sync dropdowns whenever any filter is updated (remove-tag, clear-all,
    // normal-filter change) or the user navigates back/forward.
    document.addEventListener(ThemeEvents.FilterUpdate, this.#syncSelectsFromURL);
    window.addEventListener('popstate', this.#syncSelectsFromURL);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    this.querySelectorAll('[data-gift-filter-select]').forEach((select) => {
      select.removeEventListener('change', this.#handleSelectChange);
    });

    document.removeEventListener(ThemeEvents.FilterUpdate, this.#syncSelectsFromURL);
    window.removeEventListener('popstate', this.#syncSelectsFromURL);
  }

  /**
   * Reads the authoritative section ID at runtime from the <results-list>
   * element rendered by main-collection.liquid and sets it on the nested
   * facets-form-component so the Section Rendering API targets the right
   * section.
   */
  #wireSectionId() {
    const resultsList = document.querySelector('results-list[section-id]');
    if (!resultsList) return;

    const sectionId = resultsList.getAttribute('section-id');
    if (!sectionId) return;

    const facetsForm = this.querySelector('facets-form-component');
    if (facetsForm) {
      facetsForm.setAttribute('section-id', sectionId);
    }
  }

  /**
   * Syncs every GIFTFINDER select to the current browser URL.
   *
   * Called after any filter:update event (which fires AFTER history.pushState
   * has already updated window.location.href) and on popstate.
   *
   * Covers these cases automatically:
   *  - A facet-remove-component tag is clicked to remove a GIFTFINDER filter.
   *  - "Clear all filters" resets the whole URL.
   *  - The user hits the browser back/forward button.
   *  - A normal filter change causes a URL update (keeps dropdowns in sync).
   */
  #syncSelectsFromURL = () => {
    const url = new URL(window.location.href);

    this.querySelectorAll('[data-gift-filter-select]').forEach((select) => {
      const value = url.searchParams.get(select.name) ?? '';

      if (select.value !== value) {
        select.value = value;
      }
    });
  };

  /**
   * Merges the current value of every GIFTFINDER select into the browser URL
   * (preserving all other filter/sort params) then delegates to
   * FacetsFormComponent.updateFiltersByURL() so the collection section
   * re-renders with the correct, complete URL.
   *
   * Uses updateFiltersByURL rather than updateFilters because:
   *   - updateFilters() resets url.search to '' before writing form data,
   *     discarding sort, price, and non-GIFTFINDER facet params.
   *   - updateFiltersByURL(url) pushes the provided URL as-is, keeping
   *     all existing params alongside the GIFTFINDER changes.
   */
  #handleSelectChange = () => {
    const facetsForm = this.querySelector('facets-form-component');
    if (!facetsForm) return;

    const url = new URL(window.location.href);

    this.querySelectorAll('[data-gift-filter-select]').forEach((select) => {
      // Always clear the stale value first so empty selection removes the param.
      url.searchParams.delete(select.name);

      if (select.value) {
        url.searchParams.set(select.name, select.value);
      }
    });

    facetsForm.updateFiltersByURL(url.toString());
  };
}

if (!customElements.get('giftcard-filter-header')) {
  customElements.define('giftcard-filter-header', GiftcardFilterHeaderComponent);
}
